import { useState } from "react";
import { Mail, Phone, MapPin, Send, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { brand } from "../mock";

export default function ContactPage() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing details", description: "Please fill in all fields.", variant: "destructive" });
      return;
    }
    toast({ title: "Message sent!", description: "We'll get back to you shortly." });
    setForm({ name: "", email: "", message: "" });
  };

  return (
    <main>
      <section className="bg-gradient-to-b from-green-50 to-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
          <p className="text-sm font-bold tracking-widest text-green-700">CONTACT</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-neutral-900">Get in touch</h1>
          <p className="mt-3 text-neutral-600 max-w-2xl mx-auto">
            Questions about meal plans, allergies, delivery zones or anything else — we&apos;re here to help.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 lg:grid-cols-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-neutral-200 p-6 hover:border-green-600 transition-colors">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-green-50 text-green-700 flex items-center justify-center"><Mail size={18} /></span>
              <div>
                <p className="text-xs text-neutral-500">Email</p>
                <a href={`mailto:${brand.email}`} className="font-semibold text-neutral-900 hover:text-green-700">{brand.email}</a>
              </div>
            </div>
            <p className="mt-3 text-sm text-neutral-600">Best for: meal changes, allergy concerns, pause/cancel requests.</p>
          </div>
          <div className="rounded-2xl border border-neutral-200 p-6 hover:border-green-600 transition-colors">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-green-50 text-green-700 flex items-center justify-center"><MessageCircle size={18} /></span>
              <div>
                <p className="text-xs text-neutral-500">WhatsApp</p>
                <a href={`https://wa.me/${brand.whatsapp.replace(/\D/g, "")}`} className="font-semibold text-neutral-900 hover:text-green-700">{brand.whatsapp}</a>
              </div>
            </div>
            <p className="mt-3 text-sm text-neutral-600">Urgent delivery issues — Mon-Fri 9am–8pm NZST.</p>
          </div>
          <div className="rounded-2xl border border-neutral-200 p-6 hover:border-green-600 transition-colors">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-green-50 text-green-700 flex items-center justify-center"><Phone size={18} /></span>
              <div>
                <p className="text-xs text-neutral-500">Phone</p>
                <a href={`tel:${brand.phone}`} className="font-semibold text-neutral-900 hover:text-green-700">{brand.phone}</a>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-neutral-200 p-6">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-green-50 text-green-700 flex items-center justify-center"><MapPin size={18} /></span>
              <div>
                <p className="text-xs text-neutral-500">Kitchen</p>
                <p className="font-semibold text-neutral-900">Auckland, New Zealand</p>
              </div>
            </div>
            <p className="mt-3 text-sm text-neutral-600">Delivering to: {brand.deliveryAreas.slice(0, 5).join(", ")} and more.</p>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-neutral-200 p-6 md:p-8 bg-white">
          <h2 className="text-2xl font-extrabold text-neutral-900">Send us a message</h2>
          <p className="mt-1 text-sm text-neutral-600">We usually reply within a few hours.</p>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="mt-1.5" />
            </div>
          </div>
          <div className="mt-4">
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" rows={6} className="mt-1.5" />
          </div>
          <Button type="submit" className="mt-6 bg-green-600 hover:bg-green-700 text-white rounded-full px-6 h-11">
            Send message <Send size={16} className="ml-1" />
          </Button>
        </form>
      </section>
    </main>
  );
}
